/**
 * SkillDelivery Platform
 *
 * Copyright © 2026 SKD RATWANI Technology & Software Solutions Pvt Ltd
 * All Rights Reserved.
 *
 * PROPRIETARY AND CONFIDENTIAL
 */

/**
 * Workflow Factory admin API client.
 *
 * Thin wrapper over adminApi.request() — keeps page components free of
 * URL strings and recentres envelope-shape handling. Every method returns
 * the raw V2 response body (already unwrapped by adminApi) so callers can
 * pull out `.data.workflows` or `.workflows` defensively.
 *
 * SSE streaming is NOT here — see services/workflowEventsClient.js
 * (Slice 3). That uses fetch + manual SSE parser because EventSource
 * cannot send the Bearer JWT this admin plane requires.
 */

import adminApi from './adminApi';

const workflowAdminApi = {
  // ─────────────────────────────────────────────────────────────────
  // Catalog
  // ─────────────────────────────────────────────────────────────────

  listWorkflows: ({ domain, state, limit } = {}) => {
    const qs = new URLSearchParams();
    if (domain) qs.set('domain', domain);
    if (state) qs.set('state', state);
    if (limit) qs.set('limit', String(limit));
    const tail = qs.toString() ? `?${qs}` : '';
    return adminApi.request(`/admin/workflows${tail}`, { method: 'GET' });
  },

  getWorkflow: (id) =>
    adminApi.request(`/admin/workflows/${encodeURIComponent(id)}`, {
      method: 'GET',
    }),

  listRecentRuns: (workflowId, { limit = 5 } = {}) =>
    adminApi.request(
      `/admin/workflows/${encodeURIComponent(workflowId)}/runs?limit=${limit}`,
      { method: 'GET' }
    ),

  // ─────────────────────────────────────────────────────────────────
  // Runs
  // ─────────────────────────────────────────────────────────────────

  startRun: (workflowId, inputs) =>
    adminApi.request(
      `/admin/workflows/${encodeURIComponent(workflowId)}/runs`,
      { method: 'POST', body: JSON.stringify({ inputs }) }
    ),

  getRun: (runId) =>
    adminApi.request(`/admin/workflow-runs/${encodeURIComponent(runId)}`, {
      method: 'GET',
    }),

  confirmRun: (runId, confirmationId, { approve, note } = {}) =>
    adminApi.request(
      `/admin/workflow-runs/${encodeURIComponent(runId)}/confirm/${encodeURIComponent(confirmationId)}`,
      {
        method: 'POST',
        body: JSON.stringify({ approve: Boolean(approve), note: note || undefined }),
      }
    ),
};

// ─────────────────────────────────────────────────────────────────────
// Response-shape helpers — V2 envelope is { success, data: { workflows: [...] } }
// in some endpoints and { workflows: [...] } in others. Treat both.
// ─────────────────────────────────────────────────────────────────────

export function extractWorkflows(body) {
  if (!body) return [];
  if (Array.isArray(body)) return body;
  if (Array.isArray(body.workflows)) return body.workflows;
  if (Array.isArray(body.data)) return body.data;
  if (Array.isArray(body.data?.workflows)) return body.data.workflows;
  if (Array.isArray(body.results)) return body.results;
  return [];
}

export function extractWorkflow(body) {
  if (!body) return null;
  if (body.workflow) return body.workflow;
  if (body.data?.workflow) return body.data.workflow;
  if (body.id || body.workflow_id) return body;
  if (body.data && (body.data.id || body.data.workflow_id)) return body.data;
  return null;
}

export function extractRuns(body) {
  if (!body) return [];
  if (Array.isArray(body)) return body;
  if (Array.isArray(body.runs)) return body.runs;
  if (Array.isArray(body.data)) return body.data;
  if (Array.isArray(body.data?.runs)) return body.data.runs;
  return [];
}

export function extractRun(body) {
  if (!body) return null;
  if (body.run) return body.run;
  if (body.data?.run) return body.data.run;
  if (body.run_id || body.id) return body;
  if (body.data && (body.data.run_id || body.data.id)) return body.data;
  return null;
}

export function extractStages(body) {
  if (!body) return [];
  if (Array.isArray(body.stages)) return body.stages;
  if (Array.isArray(body.data?.stages)) return body.data.stages;
  return [];
}

export default workflowAdminApi;
